import React, { useState, createContext, useContext, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../Utils/api";
import { timesheetStatus } from "../../Utils/constants";

const apiPath = {
  fetchTimesheets: "/timesheet/gettimesheets",
  fetchTimesheetsForJob: "/timesheet/gettimesheetsforjob",
  fetchTimesheetDetails: "/timesheet/gettimesheetdetails",
  addNewTimesheet: "/timesheet/addtimesheet",
  updateTimesheet: "/timesheet/updatetimesheet",
  fetchTimesheetsByDate: "/timesheet/gettimesheetbydate",
  generateSecureLink: "/timesheet/link/update",
};

const nullFn = () => null;
const TimesheetContext = createContext({
  loading: false,
  timesheetMaster: {},
  timesheetDetails: {},
  selectedTimesheet: null,
  setSelectedTimesheet: nullFn,
  selectedJobDetails: {},
  setSelectedJobDetails: nullFn,
  addNewTimesheet: nullFn,
  updateTimesheet: nullFn,
  timesheets: [],
  fetchTimesheets: nullFn,
  fetchTimesheetsByDate: nullFn,
  generateSecureLink: nullFn,
  timesheetOtherDetails: {},
});

export const useTimesheetProvider = () => useContext(TimesheetContext);
const TimesheetProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetMaster, setTimesheetMaster] = useState({});
  const [timesheetDetails, setTimesheetDetails] = useState({});
  const [selectedJobDetails, setSelectedJobDetails] = useState({});
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [timesheetOtherDetails, setTimesheetOtherDetails] = useState({});

  useEffect(() => {
    if (selectedJobDetails?.job?.id)
      fetchTimesheetsForJob(selectedJobDetails?.job?.id);
  }, [selectedJobDetails?.job?.id]);

  useEffect(() => {
    if (selectedTimesheet) fetchTimesheetDetails(selectedTimesheet);
  }, [selectedTimesheet]);

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheets, {
        params: {
          userId: localStorage.getItem("userId"),
          userType: "CONSULTANT",
        },
      });

      if (res.status === 200 && res.data?.success) {
        setTimesheets(res.data.result);
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetsForJob = async (job_id) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheetsForJob, {
        params: { job_id },
      });

      if (res.status === 200 && res.data?.success) {
        setTimesheetMaster({ ...timesheetMaster, [job_id]: res.data.result });
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetsByDate = async ({ jobId, startDate }) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.fetchTimesheetsByDate, {
        jobId,
        startDate,
        userId: localStorage.getItem("userId"),
        userType: "CONSULTANT",
      });

      if (res.status === 200 && res.data?.success) {
        if (res.data.msg !== "No timesheet found") {
          const cur = timesheetMaster[jobId];
          if (cur.findIndex((t) => t.id === res.data.timesheet.id) === -1) {
            setTimesheetMaster({
              ...timesheetMaster,
              [jobId]: [
                ...(Array.isArray(timesheetMaster[jobId])
                  ? timesheetMaster[jobId]
                  : []),
                res.data.timesheet,
              ],
            });
          } else {
            const newTimesheetMaster = timesheetMaster[jobId].map((t) => {
              return t.id === res.data.timesheet.id ? res.data.timesheet : t;
            });

            setTimesheetMaster({
              ...timesheetMaster,
              [jobId]: newTimesheetMaster,
            });
          }

          setTimesheetDetails({
            ...timesheetDetails,
            [res.data.timesheet.id]: res.data.result,
          });

          setSelectedTimesheet(res.data.timesheet.id);
        } else {
          setSelectedTimesheet(null);
        }
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetDetails = async (timesheet_id) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheetDetails, {
        params: { timesheet_id },
      });

      if (res.status === 200 && res.data?.success) {
        setTimesheetDetails({
          ...timesheetDetails,
          [timesheet_id]: res.data.result,
        });
        console.log(res.data?.timesheet[0]);
        setTimesheetOtherDetails(res.data?.timesheet);
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const addNewTimesheet = async ({
    start,
    end,
    dayDetails,
    submit = false,
  }) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.addNewTimesheet, {
        job_id: selectedJobDetails?.job?.id,
        created_by: localStorage.getItem("userId"),
        weekDetails: {
          start,
          end,
          status: submit ? timesheetStatus.submitted : timesheetStatus.saved,
        },
        dayDetails,
      });

      console.log(res);

      if (res.status === 200 && res.data?.success) {
        const timesheets = timesheetMaster[selectedJobDetails?.job?.id];
        timesheets.push(res.data.timesheet);

        setTimesheetMaster({
          ...timesheetMaster,
          [selectedJobDetails?.job?.id]: timesheets,
        });

        setSelectedTimesheet(res.data.timesheet.id);
        setTimesheetDetails({
          ...timesheetDetails,
          [res.data.timesheet.id]: res.data.result,
        });

        setLoading(false);
        if (timesheetStatus.saved) {
          Swal.fire({
            title: "Success",
            text: "Timesheet saved successfully",
            icon: "success",
          });
        } else {
          Swal.fire({
            title: "Success",
            text: "Timesheet created successfully",
            icon: "success",
          });
        }
      } else {
        throw new Error("Error occurred while creating timesheet");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        title: "Error",
        text: "Error occurred while creating timesheet",
        icon: "error",
      });
    }
  };

  const updateTimesheet = async ({ dayData, submit }) => {
    setLoading(true);
    try {
      console.log(dayData, submit);
      const res = await api.put(apiPath.updateTimesheet, {
        id: selectedTimesheet,
        status: submit ? timesheetStatus.submitted : timesheetStatus.saved,
        dayData,
      });

      if (res.status === 200 && res.data?.success) {
        let timesheets = timesheetMaster[selectedJobDetails?.job?.id];
        const timesheetIndex = timesheets.findIndex(
          (timesheet) => timesheet.id === selectedTimesheet
        );

        timesheets[timesheetIndex] = res.data.timesheet;
        setTimesheetMaster({
          ...timesheetMaster,
          [selectedJobDetails?.job?.id]: timesheets,
        });

        setTimesheetDetails({
          ...timesheetDetails,
          [selectedTimesheet]: res.data.result,
        });

        setLoading(false);
        Swal.fire({
          title: "Success",
          text: `Timesheet ${submit ? "submitted" : "updated"} successfully`,
          icon: "success",
          timer: 1500,
        });
      } else {
        throw new Error("Error occurred while updating timesheet");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        title: "Error",
        text: "Error occurred while updating timesheet",
        icon: "error",
        timer: 1500,
      });
    }
  };

  const generateSecureLink = async ({
    timesheetId,
    emails,
    pin,
    numberOfWeeks,
  }) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.generateSecureLink, {
        timesheetId,
        emails,
        pin,
        numberOfWeeks,
      });

      if (res.status === 200 && res.data?.success) {
        setLoading(false);
        return res.data.result;
      } else {
        throw new Error("Error occurred while generating secure link");
      }
    } catch (e) {
      setLoading(false);
      console.log(e);
      Swal.fire({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
      });

      return null;
    }
  };

  const value = {
    loading,
    timesheetMaster,
    timesheetDetails,
    selectedTimesheet,
    setSelectedTimesheet,
    selectedJobDetails,
    setSelectedJobDetails,
    addNewTimesheet,
    updateTimesheet,
    timesheets,
    fetchTimesheets,
    fetchTimesheetsByDate,
    generateSecureLink,
    timesheetOtherDetails,
  };

  return (
    <TimesheetContext.Provider value={value}>
      {children}
    </TimesheetContext.Provider>
  );
};

export default TimesheetProvider;
